import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { 
  MapPin, Globe, Clock, ChevronLeft, Loader2, 
  Edit, Trash2, Shield, Lock, Check, X, Mail
} from "lucide-react";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "../../contexts/AuthContext";
import { Section, Button } from "./PageComponents";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyPenPalProfilePage() {
  const { user, checkAuth } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (user?.penpal_profile_id) {
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/penpal/profile`, { withCredentials: true });
      setProfile(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/penpal/profile`, { withCredentials: true });
      await checkAuth();
      window.location.hash = "#penpal";
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setDeleting(false);
    }
  };

  if (!user) {
    return (
      <Section>
        <div className="text-center">
          <h2 className="text-3xl font-serif mb-4">Please sign in first</h2>
          <Button href="#login">Sign In</Button>
        </div>
      </Section>
    );
  }

  if (loading) {
    return (
      <Section>
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-lws-green" />
        </div>
      </Section>
    );
  }

  if (!profile) {
    return (
      <Section>
        <div className="text-center">
          <h2 className="text-3xl font-serif mb-4">No Profile Found</h2>
          <p className="text-lws-taupe mb-8">Create your pen pal profile to get started.</p>
          <Button href="#penpal-create">Create Profile</Button>
        </div>
      </Section>
    );
  }

  return (
    <Section id="penpal-profile">
      <div className="max-w-3xl mx-auto" data-testid="my-profile-page">
        <a href="#penpal" className="inline-flex items-center text-xs uppercase tracking-widest text-lws-taupe hover:text-lws-green mb-8 transition-colors">
          <ChevronLeft size={16} className="mr-2" /> Back to Pen Pals
        </a>
        
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-serif text-lws-green">{profile.display_name}</h1>
            <p className="text-lws-taupe flex items-center gap-2 mt-2">
              <MapPin size={16} /> {profile.country}
            </p>
          </div>
          
          <div className="flex gap-2">
            <a 
              href="#penpal-connections"
              className="px-4 py-2 border border-lws-ink/20 text-sm hover:border-lws-green transition-colors"
            >
              My Connections
            </a>
            <a 
              href="#email-preferences"
              className="px-4 py-2 border border-lws-ink/20 text-sm hover:border-lws-green transition-colors flex items-center gap-2"
            >
              <Mail size={16} />
              Email Settings
            </a>
          </div>
        </div>
        
        {error && (
          <div className="bg-red-50 text-red-600 p-4 mb-8">
            {error}
          </div>
        )}
        
        <div className="space-y-8">
          {/* Bio */}
          <div className="border border-lws-ink/5 p-6">
            <h3 className="text-xs uppercase tracking-widest text-lws-taupe mb-4">About Me</h3>
            <p className="text-lws-ink leading-relaxed">{profile.bio}</p>
          </div>
          
          {/* Preferences */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-lws-ink/5 p-6">
              <h3 className="text-xs uppercase tracking-widest text-lws-taupe mb-4">Preferences</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-lws-taupe">Location Preference</span>
                  <span className="capitalize">{profile.location_preference.replace("_", " ")}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-lws-taupe">Writing Frequency</span>
                  <span className="capitalize">{profile.writing_frequency}</span>
                </div>
              </div>
            </div>
            
            <div className="border border-lws-ink/5 p-6">
              <h3 className="text-xs uppercase tracking-widest text-lws-taupe mb-4">Languages</h3>
              <div className="flex flex-wrap gap-2">
                {profile.languages.map((lang, idx) => (
                  <span key={idx} className="bg-lws-taupe/10 px-3 py-1 text-sm">{lang}</span>
                ))}
              </div>
            </div>
          </div>
          
          {/* Interests */}
          <div className="border border-lws-ink/5 p-6">
            <h3 className="text-xs uppercase tracking-widest text-lws-taupe mb-4">Interests</h3>
            <div className="flex flex-wrap gap-2">
              {profile.interests.map((interest, idx) => (
                <span key={idx} className="bg-lws-amber/20 px-3 py-1 text-sm">{interest}</span>
              ))}
            </div>
          </div>
          
          {/* Address */}
          <div className="border border-lws-ink/5 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Lock size={16} className="text-lws-green" />
              <h3 className="text-xs uppercase tracking-widest text-lws-taupe">Mailing Address (Private)</h3>
            </div>
            
            {profile.use_forwarding_service ? (
              <div className="bg-lws-green/10 p-4">
                <p className="text-sm text-lws-green flex items-center gap-2">
                  <Shield size={16} />
                  Using Letter Writers Society Forwarding Service
                </p>
                <p className="text-xs text-lws-taupe mt-2">Your home address is kept completely private</p>
              </div>
            ) : (
              <div className="text-sm space-y-1">
                {profile.address?.address_line1 && <p>{profile.address.address_line1}</p>}
                {profile.address?.address_line2 && <p>{profile.address.address_line2}</p>}
                <p>
                  {profile.address?.city}{profile.address?.state ? `, ${profile.address.state}` : ""} {profile.address?.postal_code}
                </p>
                {profile.address?.address_country && <p>{profile.address.address_country}</p>}
              </div>
            )}
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 gap-6">
            <div className="border border-lws-ink/5 p-6 text-center">
              <p className="text-4xl font-serif text-lws-green">{profile.match_count || 0}</p>
              <p className="text-xs uppercase tracking-widest text-lws-taupe mt-2">Successful Matches</p>
            </div>
            <div className="border border-lws-ink/5 p-6 text-center">
              <p className="text-sm text-lws-taupe">Member since</p>
              <p className="font-serif">{new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
            </div>
          </div>
          
          {/* Danger Zone */}
          <div className="border border-red-200 p-6">
            <h3 className="text-xs uppercase tracking-widest text-red-600 mb-4">Danger Zone</h3>
            <p className="text-sm text-lws-taupe mb-4">
              Deleting your profile will remove all your data and connections. This action cannot be undone.
            </p>
            
            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-4 py-2 border border-red-300 text-red-600 text-sm hover:bg-red-50 transition-colors"
              >
                Delete Profile
              </button>
            ) : (
              <div className="flex items-center gap-4">
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 text-white text-sm hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                  Confirm Delete
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 border border-lws-ink/20 text-sm hover:border-lws-green transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
}
